import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDateShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileText,
  ReceiptText,
  Truck,
  DollarSign,
  RefreshCw,
  ArrowUpRight,
  Store,
} from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, tone = "default", testid }) => {
  const toneClass = {
    default: "text-foreground",
    invoice: "text-[#1d4ed8]",
    sales: "text-[#0f766e]",
    warn: "text-[#b45309]",
  }[tone];
  return (
    <div className="stat-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className={`mt-3 font-heading text-3xl font-bold tracking-tight num ${toneClass}`}>{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-md bg-secondary grid place-items-center text-primary">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
};

const typeLabel = (t) => (t === "invoice" ? "Quote" : t === "sales_order" ? "Order" : t);

export default function ApplianceDashboard() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/appliance/dashboard");
    setData(data);
  };

  useEffect(() => {
    load();
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/appliance/sync");
      if (data.ok === false) toast.error(data.error || "Sync failed");
      else toast.success(`Synced ${data.synced} documents (${data.inserted} new)`);
      await load();
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const t = data.totals;

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in" data-testid="appliance-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Appliance Depot</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Documents overview</h1>
          <p className="text-muted-foreground mt-1">Orders and quotes synced from Base44.</p>
        </div>
        <Button
          data-testid="appliance-sync-btn"
          variant="outline"
          onClick={sync}
          disabled={syncing}
          className="border-border"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
          Sync documents
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          icon={FileText}
          label="Orders (revenue)"
          value={fmtCurrency(t.sales_order_total)}
          sub={`${t.sales_order_count} confirmed orders`}
          tone="sales"
          testid="appliance-kpi-orders"
        />
        <KpiCard
          icon={ReceiptText}
          label="Quotes (pipeline)"
          value={fmtCurrency(t.invoice_total)}
          sub={`${t.invoice_count} quotes · not yet confirmed`}
          tone="invoice"
          testid="appliance-kpi-quotes"
        />
        <KpiCard
          icon={Truck}
          label="Delivery costs"
          value={fmtCurrency(t.delivery_costs)}
          sub="Across all documents"
          tone="warn"
          testid="appliance-kpi-delivery"
        />
        <KpiCard
          icon={DollarSign}
          label="Documents"
          value={t.document_count}
          sub={`${t.sales_order_count} orders · ${t.invoice_count} quotes`}
          testid="appliance-kpi-documents"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent documents */}
        <div className="surface-card lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-eyebrow">Recent documents</div>
              <h2 className="font-heading text-xl font-semibold mt-1">Latest pulls from Base44</h2>
            </div>
            <Link
              to="/appliance/documents"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              data-testid="appliance-view-all-documents"
            >
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="divide-y divide-border/70">
            {data.recent_documents.map((d) => (
              <Link
                key={d.id}
                to={`/appliance/documents/${d.id}`}
                data-testid={`appliance-recent-doc-${d.id}`}
                className="grid grid-cols-12 gap-3 py-3 items-center hover:bg-secondary/50 -mx-3 px-3 rounded-md transition-colors"
              >
                <div className="col-span-4 sm:col-span-3 font-mono text-sm">{d.document_number}</div>
                <div className="col-span-8 sm:col-span-5 text-sm truncate">
                  <div className="font-medium truncate">{d.customer || "—"}</div>
                  <div className="text-xs text-muted-foreground">
                    {typeLabel(d.document_type)} · {fmtDateShort(d.created_date)}
                  </div>
                </div>
                <div className="hidden sm:block sm:col-span-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${d.document_type === "invoice" ? "border-[#1d4ed8]/30 text-[#1d4ed8] bg-[#1d4ed8]/5" : "border-[#0f766e]/30 text-[#0f766e] bg-[#0f766e]/5"}`}>
                    {typeLabel(d.document_type)}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-2 text-right num font-semibold">{fmtCurrency(d.total)}</div>
              </Link>
            ))}
            {data.recent_documents.length === 0 && (
              <div className="py-8 text-center text-muted-foreground text-sm">No documents yet. Sync from Base44.</div>
            )}
          </div>
        </div>

        {/* Top dealers */}
        <div className="surface-card p-6">
          <div className="flex items-center gap-2 mb-5">
            <Store className="w-4 h-4 text-primary" />
            <h2 className="font-heading text-xl font-semibold">Top dealers</h2>
            <span className="text-xs text-muted-foreground ml-auto">by order revenue</span>
          </div>
          <div className="space-y-3">
            {data.top_dealers.map((dl) => (
              <div key={dl.name} className="flex items-center justify-between text-sm" data-testid={`appliance-dealer-${dl.name}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{dl.name}</div>
                  <div className="text-xs text-muted-foreground">{dl.count} documents</div>
                </div>
                <div className="text-right">
                  <div className="num font-semibold text-[#0f766e]">{fmtCurrency(dl.sales_order_total)}</div>
                  {dl.invoice_total > 0 && (
                    <div className="text-xs text-muted-foreground num">{fmtCurrency(dl.invoice_total)} quoted</div>
                  )}
                </div>
              </div>
            ))}
            {data.top_dealers.length === 0 && (
              <div className="py-6 text-center text-muted-foreground text-sm">No dealer data.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
